/**
 * شاشة «تأكيد البريد» — الصفحة اللي رابط الإيميل بيوديها.
 *
 * 🔴 **الشاشة دي كانت ناقصة تمامًا (اتكشفت ٢٠٢٦-٠٩-١٩).**
 *    الباك إند بيبعت `/verify-email?token=…` من أول يوم، والواجهة مكانش
 *    فيها المسار ده — فالموظفة اللي تدوس الرابط كانت بتترمي على شاشة
 *    الدخول، تفتكر إنها غلطت، تحاول بكلمة سر مش متسجّلة، والحساب يتقفل
 *    من المحاولات المتكررة. عملت كل حاجة صح وكانت بتدوس على باب مقفول
 *    ومكتوب عليه «ادخل».
 *    ⚠️ والعطل عاش مستخبّي لأن الإيميل نفسه مكانش بيخرج — أول ما الإرسال
 *       اشتغل، العطل اللي بعده ظهر في نفس اليوم.
 *
 * ⛔ **التأكيد ما بيدّيش أي وصول.** بيحوّل الحساب من «مستني تأكيد البريد»
 *    لـ«تحت المراجعة» وبس — والاعتماد قرار بشري بعد كده.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { Link, LOGIN_PATH } from "@/lib/router";
import { errorMessageAr, OfflineError } from "@/api/client";
import { verifyEmail } from "@/api/auth";
import "@/styles/auth.css";

type State =
  | { kind: "working" }
  | { kind: "no-token" }
  | { kind: "done"; messageAr: string; active: boolean }
  | { kind: "failed"; messageAr: string };

export function VerifyEmail() {
  const [state, setState] = useState<State>({ kind: "working" });
  // ⚠️ حارس ضد التشغيل مرتين: التوكن **استخدام واحد**، والنداء التاني
  //    بيرجع «مش صالح» ويقلب شاشة ناجحة لشاشة فشل قدام الموظفة.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = new URLSearchParams(window.location.search).get("token") || "";
    if (!token) {
      setState({ kind: "no-token" });
      return;
    }

    void (async () => {
      try {
        const res = await verifyEmail(token);
        setState({
          kind: "done",
          messageAr: res.messageAr,
          active: res.status === "active",
        });
      } catch (err) {
        if (!(err instanceof OfflineError)) {
          console.error("[verify-email] تأكيد البريد فشل", err);
        }
        setState({ kind: "failed", messageAr: errorMessageAr(err) });
      }
    })();
  }, []);

  return (
    <div className="auth" dir="rtl">
      <section className="auth-pane form">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="brand-mark">
              <Icon name="book" size={20} stroke="#0F1D33" />
            </div>
            <div>
              <div className="brand-name">رتّل</div>
              <div className="brand-sub">السيستم الإداري</div>
            </div>
          </div>

          <div style={{ marginTop: 40 }}>
            {state.kind === "working" ? (
              <>
                <div className="auth-title">بنأكّد بريدك…</div>
                <div className="t-desc" style={{ marginTop: 10 }}>
                  ثانية واحدة بس
                </div>
              </>
            ) : null}

            {state.kind === "no-token" ? (
              <>
                <div className="auth-title">الرابط ناقص</div>
                <div className="t-desc" style={{ marginTop: 10 }}>
                  افتح الرابط من الرسالة اللي وصلتك كامل. ولو نسخته بإيدك،
                  يمكن يكون اتقص — دوس عليه من جوّه الرسالة نفسها.
                </div>
              </>
            ) : null}

            {state.kind === "done" ? (
              <>
                <div className="auth-title">
                  {state.active ? "حسابك شغّال" : "تمام — بريدك اتأكّد"}
                </div>
                <div className="t-desc" style={{ marginTop: 10 }}>
                  {state.messageAr}
                </div>
                {/* 🔑 الخطوة اللي بعدها لازم تتقال صريحة. «اتأكّد» لوحدها
                    بتخلّي الموظفة تجرّب تدخل وتلاقي شاشة مراجعة وتفتكر
                    إن فيه عطل. */}
                {!state.active ? (
                  <div className="t-desc" style={{ marginTop: 14 }}>
                    إدارة الأكاديمية بتراجع طلبك وتحدّد قسمك. تقدر تسجّل دخول
                    دلوقتي — هتلاقي شاشة «تحت المراجعة» لحد ما الحساب يتفعّل،
                    وهيوصلك إشعار.
                  </div>
                ) : null}
              </>
            ) : null}

            {state.kind === "failed" ? (
              <>
                <div className="auth-title">الرابط ده ما نفعش</div>
                <div
                  className="err-box"
                  style={{ marginTop: 20 }}
                  role="alert"
                  aria-live="assertive"
                >
                  <Icon name="alert" size={18} stroke="#B42318" />
                  <span className="t-body" style={{ color: "var(--danger)" }}>
                    {state.messageAr}
                  </span>
                </div>
                {/* ⚠️ أشهر سببين، ومكتوبين عشان ما تحاولش بكلمة سر غلط
                    لحد ما الحساب يتقفل — ده اللي حصل فعلًا مرة. */}
                <div className="t-desc" style={{ marginTop: 16 }}>
                  الأغلب واحد من اتنين: الرابط قديم (لو وصلتك أكتر من رسالة،
                  افتح آخر واحدة)، أو عدّت مدة صلاحيته.
                </div>
                <div className="t-desc" style={{ marginTop: 10 }}>
                  ⛔ ما تحاولش تدخل بكلمة سر تخمّنها — المحاولات الغلط بتقفل
                  الحساب مؤقتًا. كلّم إدارة الأكاديمية وهي تبعتلك رابط جديد.
                </div>
              </>
            ) : null}

            {state.kind !== "working" ? (
              <div style={{ marginTop: 28 }}>
                <Link to={LOGIN_PATH} className="link">
                  الرجوع لصفحة الدخول
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
